// TooltipWrapper.jsx
import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Overlay, Tooltip } from "react-bootstrap";
import "./TooltipWrapper.css";

export default function TooltipWrapper({
    tooltipText,
    children,
    tooltipId = "tooltip",
    placement = "bottom", // default requested placement
    modalVisible,
}) {
    const [showTooltip, setShowTooltip] = useState(false);
    const [containerEl, setContainerEl] = useState(null);
    const [computedPlacement, setComputedPlacement] = useState(placement);
    const targetRef = useRef(null);

    // auto-detect the best container (modal body / content / dialog / modal, otherwise document.body)
    useLayoutEffect(() => {
        if (typeof document === "undefined" || !targetRef.current) return;
        const el =
            targetRef.current.closest(".modal-body") ||
            targetRef.current.closest(".modal-content") ||
            targetRef.current.closest(".modal-dialog") ||
            targetRef.current.closest(".modal") ||
            document.body;
        setContainerEl(el);
    }, [targetRef.current]);

    // hide tooltip when modal closes (if parent passes this prop)
    useLayoutEffect(() => {
        if (modalVisible === false) setShowTooltip(false);
    }, [modalVisible]);

    // compute best initial placement before we open the tooltip
    function computeBestPlacement(preferred = placement) {
        if (!targetRef.current) return preferred;
        const rect = targetRef.current.getBoundingClientRect();

        // boundary rect depends on whether we attached to modal container or to body
        const boundaryRect =
            containerEl && containerEl !== document.body
                ? containerEl.getBoundingClientRect()
                : { top: 0, bottom: typeof window !== "undefined" ? window.innerHeight : 0 };

        const spaceBelow = boundaryRect.bottom - rect.bottom;
        const spaceAbove = rect.top - boundaryRect.top;

        // small threshold so we don't flip for tiny differences
        const MIN_SPACE = 90;

        if (preferred === "bottom" || preferred === "auto") {
            return spaceBelow < MIN_SPACE && spaceAbove > spaceBelow ? "top" : "bottom";
        }
        if (preferred === "top") {
            return spaceAbove < MIN_SPACE && spaceBelow > spaceAbove ? "bottom" : "top";
        }
        return preferred;
    }

    // Popper config: switch strategy depending on container
    const popperConfig = useMemo(() => {
        const inModal = !!containerEl && containerEl !== document.body;
        return {
            strategy: inModal ? "absolute" : "fixed",
            modifiers: [
                // try to flip if initial placement not fit
                { name: "flip", options: { fallbackPlacements: ["top", "right", "left", "bottom"] } },
                // keep overlay inside boundary
                {
                    name: "preventOverflow",
                    options: {
                        // if inside modal, keep within modal; otherwise keep within viewport
                        boundary: inModal ? containerEl : "viewport",
                        rootBoundary: "viewport",
                    },
                },
                // reduce jitter when ancestor transforms exist
                { name: "computeStyles", options: { adaptive: false } },
                { name: "eventListeners", options: { scroll: true, resize: true } },
            ],
        };
    }, [containerEl]);

    // enhanced child: attach ref and handlers (preserves child's handlers)
    const enhancedChild = React.cloneElement(children, {
        ref: (node) => {
            // preserve forwarded refs if user supplied one
            targetRef.current = node;
            const childRef = children.ref;
            if (!childRef) return;
            if (typeof childRef === "function") childRef(node);
            else if (typeof childRef === "object") childRef.current = node;
        },
        onMouseEnter: (e) => {
            // compute placement immediately before showing
            const next = computeBestPlacement(placement);
            setComputedPlacement(next);
            setShowTooltip(true);
            children.props.onMouseEnter?.(e);
        },
        onMouseLeave: (e) => {
            setShowTooltip(false);
            children.props.onMouseLeave?.(e);
        },
        onClick: (e) => {
            // hide on click if you want (optional)
            setShowTooltip(false);
            children.props.onClick?.(e);
        },
    });

    return (
        <>
            {enhancedChild}
            <Overlay
                target={targetRef.current}
                show={showTooltip}
                placement={computedPlacement}
                container={containerEl || (typeof document !== "undefined" ? document.body : undefined)}
                popperConfig={popperConfig}
                delay={{ show: 200, hide: 100 }}
            >
                {(props) => (
                    <Tooltip id={tooltipId} {...props}>
                        {tooltipText}
                    </Tooltip>
                )}
            </Overlay>
        </>
    );
}